import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskLibraryApi } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { SlideDrawer } from '../components/ui/slide-drawer';
import {
  Loader2, Plus, Trash2, Search, X, Wrench, ChevronRight, ChevronUp, ChevronDown, Clock, Copy, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { useConfirm } from '../components/useConfirm';

const CATEGORIES = ['Scheduled Service', 'Repair', 'Inspection', 'Pre-Delivery', 'Commissioning', 'General'];
const CAT_META = {
  'Scheduled Service': { text: '#8A6D00', fill: '#FFF8D6', border: '#EFE0A0', dot: '#E0B800' },
  Repair: { text: '#B91C1C', fill: '#FDECEC', border: '#F3C9C9', dot: '#B91C1C' },
  Inspection: { text: '#047857', fill: '#ECFDF5', border: '#A7F3D0', dot: '#10B981' },
  'Pre-Delivery': { text: '#3D3A35', fill: '#F6F4EF', border: '#DAD5CA', dot: '#6B6660' },
  Commissioning: { text: '#3D3A35', fill: '#F6F4EF', border: '#DAD5CA', dot: '#6B6660' },
  General: { text: '#3D3A35', fill: '#F6F4EF', border: '#DAD5CA', dot: '#B5AFA3' },
};
const catMeta = (c) => CAT_META[normalizeCat(c)] || CAT_META.General;
const NORM = { scheduled_service: 'Scheduled Service', repair: 'Repair', inspection: 'Inspection', pre_delivery: 'Pre-Delivery', commissioning: 'Commissioning', general: 'General' };
const normalizeCat = (c) => { if (!c) return 'General'; if (CATEGORIES.includes(c)) return c; const k = c.toLowerCase().replace(/[\s-]/g, '_'); return NORM[k] || c; };
const stepsOf = (t) => t.steps || [];
const usedLabel = (t) => (t.times_used ? `×${t.times_used}` : '—');
const totalMins = (t) => stepsOf(t).reduce((s, x) => s + (x.estimated_minutes || 0), 0);
const hasUntimed = (t) => stepsOf(t).some(x => !x.estimated_minutes);
const untimedCount = (t) => stepsOf(t).filter(x => !x.estimated_minutes).length;
const fmtDur = (mins) => {
  if (!mins) return '0m';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
};

const CategoryPill = ({ category }) => {
  const m = catMeta(category);
  return <span className="inline-flex items-center gap-1.5 font-semibold" style={{ fontSize: 11.5, color: m.text, background: m.fill, border: `1px solid ${m.border}`, borderRadius: 999, padding: '2px 10px' }}><span style={{ width: 5, height: 5, borderRadius: 999, background: m.dot }} />{normalizeCat(category)}</span>;
};
const FilterPills = ({ options, value, onChange, testId }) => (
  <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1 flex-wrap" data-testid={testId}>
    {options.map(o => {
      const active = value === o.id;
      return (
        <button key={o.id} type="button" onClick={() => onChange(o.id)} data-testid={o.testId}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
          {o.label}{o.count != null && <span className={`text-[11px] font-bold ${active ? 'text-black/70' : 'text-muted-foreground'}`}>{o.count}</span>}
        </button>
      );
    })}
  </div>
);

// ── Detail body ──────────────────────────────────────────────────────
const DetailBody = ({ t, formatDate, onEdit, onDuplicate, onDelete, onAddToWorkOrder }) => {
  const steps = stepsOf(t); const total = totalMins(t);
  let running = 0;
  return (
    <div className="space-y-3.5">
      <div>
        <h2 className="font-bold" style={{ fontSize: 19, fontFamily: 'Manrope, sans-serif' }}>{t.name}</h2>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap"><CategoryPill category={t.category} />{t.description && <span className="text-[#6B6660]" style={{ fontSize: 12.5 }}>{t.description}</span>}</div>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(2,minmax(0,1fr))' }}>
        {[
          ['Steps', steps.length],
          ['Estimated', fmtDur(total) + (hasUntimed(t) ? '+' : '')],
          ['Times used', usedLabel(t)],
          ['Last used', t.last_used_at ? formatDate(t.last_used_at) : 'Never'],
        ].map(([l, v], i) => (
          <div key={i} className="bg-white" style={{ border: '1px solid #E6E2D9', borderRadius: 10, padding: '12px 14px' }} data-testid={`detail-stat-${l.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="font-mono font-bold text-[#1A1A1A]" style={{ fontSize: 19 }}>{v}</div>
            <div className="uppercase tracking-[0.1em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ border: '1px solid #E6E2D9', borderRadius: 10, overflow: 'hidden' }}>
        <div className="flex items-center justify-between px-3.5 py-2.5 bg-[#F6F4EF] border-b border-[#E6E2D9]">
          <span className="uppercase tracking-[0.09em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>Steps <span className="font-mono ml-1 text-[#8F8A7F]">{steps.length}</span></span>
          <span className="uppercase tracking-[0.09em] text-[#8F8A7F]" style={{ fontSize: 10, fontWeight: 700 }}>Running total</span>
        </div>
        {steps.map((s, i) => { running += (s.estimated_minutes || 0); return (
          <div key={i} className="grid items-start border-b border-[#F4F2ED] last:border-0" style={{ gridTemplateColumns: '26px 1fr 62px 66px', padding: '10px 14px' }}>
            <span className="font-mono" style={{ fontSize: 12, color: '#B5AFA3' }}>{i + 1}</span>
            <div><p className="text-[#1A1A1A]" style={{ fontSize: 13.5 }}>{s.description}</p>{s.notes && <p className="text-[#6B6660] mt-0.5" style={{ fontSize: 12 }}>{s.notes}</p>}</div>
            <span className="font-mono text-right" style={{ fontSize: 12.5, color: s.estimated_minutes ? '#3D3A35' : '#B5AFA3' }}>{s.estimated_minutes ? `${s.estimated_minutes}m` : '—'}</span>
            <span className="font-mono text-right" style={{ fontSize: 11.5, color: '#B5AFA3' }}>{fmtDur(running)}</span>
          </div>
        ); })}
        {hasUntimed(t) && <div style={{ background: '#FFFDF5', padding: '9px 14px', fontSize: 12, color: '#6B5500' }}>{untimedCount(t)} step{untimedCount(t) === 1 ? '' : 's'} {untimedCount(t) === 1 ? 'has' : 'have'} no estimate, so the total is a floor, not a forecast.</div>}
      </div>
      <div style={{ border: '1px solid #E6E2D9', borderRadius: 10, overflow: 'hidden' }}>
        <div className="px-3.5 py-2.5 bg-[#F6F4EF] border-b border-[#E6E2D9] uppercase tracking-[0.09em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>Template details</div>
        {[['Category', normalizeCat(t.category)], ['Created by', t.created_by_name || '—'], ['Created', formatDate(t.created_at)]].map(([k, v], i) => (
          <div key={i} className="grid items-center border-b border-[#F4F2ED] last:border-0" style={{ gridTemplateColumns: '150px 1fr', padding: '10px 14px' }}>
            <span className="text-[#8F8A7F]" style={{ fontSize: 12 }}>{k}</span><span className="text-[#1A1A1A]" style={{ fontSize: 13 }}>{v}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 pt-3.5 border-t border-[#DAD5CA]">
        <Button onClick={onAddToWorkOrder} className="btn-industrial h-[38px]" data-testid="drawer-add-to-wo-btn"><Wrench className="w-4 h-4 mr-1.5" />Add to work order</Button>
        <Button variant="outline" onClick={onEdit} className="h-[38px]" data-testid="drawer-edit-template-btn"><Pencil className="w-4 h-4 mr-1.5" />Edit</Button>
        <Button variant="outline" onClick={onDuplicate} className="h-[38px]" data-testid="drawer-duplicate-template-btn"><Copy className="w-4 h-4 mr-1.5" />Duplicate</Button>
        <Button variant="outline" onClick={onDelete} className="h-[38px] ml-auto" style={{ color: '#B91C1C', borderColor: '#E3B5AF' }} data-testid="drawer-delete-template-btn"><Trash2 className="w-4 h-4 mr-1.5" />Delete</Button>
      </div>
    </div>
  );
};

// ── Form body ────────────────────────────────────────────────────────
const FormBody = ({ existing, onSuccess, onCancel }) => {
  const isEdit = !!existing;
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: existing?.name || '', description: existing?.description || '', category: normalizeCat(existing?.category) });
  const [steps, setSteps] = useState(
    (existing?.steps?.length > 0 ? existing.steps : [{ description: '', estimated_minutes: '', notes: '' }])
      .map(s => ({ description: s.description || '', estimated_minutes: s.estimated_minutes ?? '', notes: s.notes || '' }))
  );
  const addStep = () => setSteps(s => [...s, { description: '', estimated_minutes: '', notes: '' }]);
  const removeStep = (i) => setSteps(s => s.length > 1 ? s.filter((_, idx) => idx !== i) : s);
  const upd = (i, f, v) => setSteps(s => s.map((st, idx) => idx === i ? { ...st, [f]: v } : st));
  const move = (i, dir) => setSteps(s => { const j = i + dir; if (j < 0 || j >= s.length) return s; const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const filled = steps.filter(s => s.description.trim()).length;
  const total = steps.reduce((a, s) => a + (parseInt(s.estimated_minutes) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    const valid = steps.filter(s => s.description.trim()).map(s => ({ description: s.description.trim(), estimated_minutes: s.estimated_minutes === '' ? null : parseInt(s.estimated_minutes), notes: s.notes?.trim() || null }));
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    if (valid.length === 0) { toast.error('Add at least one step'); return; }
    setSubmitting(true);
    try {
      if (isEdit) { await taskLibraryApi.update(existing.id, { ...form, steps: valid }); toast.success('Template updated'); }
      else { await taskLibraryApi.create({ ...form, steps: valid }); toast.success('Template saved'); }
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong'); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="flex flex-col h-full">
      <div className="flex-1 space-y-4">
        <div>
          <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Template name</label>
          <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. 500hr Service — Liebherr LTM" required data-testid="template-name-input" className="h-10 border-[#C9C3B6] rounded-lg" />
        </div>
        <div>
          <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-2" style={{ fontSize: 11, fontWeight: 700 }}>Category</label>
          <FilterPills value={form.category} onChange={(c) => setForm(p => ({ ...p, category: c }))} testId="category-select" options={CATEGORIES.map(c => ({ id: c, label: c }))} />
        </div>
        <div>
          <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Description</label>
          <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="When a workshop manager should reach for this template" rows={2} className="border-[#C9C3B6] rounded-lg" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Steps <span className="font-mono text-[#8F8A7F] ml-1">{filled} of {steps.length} filled</span></span>
            <Button type="button" variant="outline" size="sm" onClick={addStep} className="h-[30px]" data-testid="add-step-btn"><Plus className="w-4 h-4 mr-1" />Add step</Button>
          </div>
          <div className="space-y-2.5">
            {steps.map((s, i) => (
              <div key={i} className="bg-white" style={{ border: '1px solid #DAD5CA', borderRadius: 10, padding: '10px 11px' }}>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col shrink-0">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30" style={{ color: '#8F8A7F' }}><ChevronUp className="w-4 h-4" /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="disabled:opacity-30" style={{ color: '#8F8A7F' }}><ChevronDown className="w-4 h-4" /></button>
                  </div>
                  <span className="font-mono shrink-0" style={{ fontSize: 12, color: '#B5AFA3' }}>{i + 1}</span>
                  <Input value={s.description} onChange={e => upd(i, 'description', e.target.value)} placeholder="What the mechanic does" className="h-[38px] flex-1 border-[#C9C3B6] rounded-lg" data-testid={`step-${i}-input`} />
                  <div className="relative shrink-0" style={{ width: 96 }}>
                    <Input value={s.estimated_minutes} onChange={e => upd(i, 'estimated_minutes', e.target.value.replace(/[^0-9]/g, ''))} placeholder="—" className="h-[38px] pr-9 font-mono border-[#C9C3B6] rounded-lg" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8F8A7F]" style={{ fontSize: 11 }}>min</span>
                  </div>
                  <button type="button" onClick={() => removeStep(i)} className="w-[30px] h-[30px] shrink-0 rounded-md flex items-center justify-center text-[#8F8A7F] hover:text-[#B91C1C] hover:bg-[#FDECEC]" data-testid={`remove-step-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-2 pl-9">
                  <Input value={s.notes} onChange={e => upd(i, 'notes', e.target.value)} placeholder="Optional note — torque figures, part numbers, cautions" className="h-8 text-[12.5px] rounded-lg" style={{ background: '#FBFAF7', border: '1px dashed #DAD5CA' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-start gap-2 text-[#3D3A35]" style={{ padding: '11px 13px', borderRadius: 9, fontSize: 12.5, background: '#F6F4EF', border: '1px solid #E2DED4' }}>
          <Clock className="w-4 h-4 shrink-0 mt-0.5 text-[#6B6660]" />
          {total > 0 ? `Estimated ${fmtDur(total)} across ${filled} step${filled === 1 ? '' : 's'}. This becomes the work order's planned duration.` : "No time estimates yet. Without them the work order cannot be scheduled against a mechanic's day."}
        </div>
      </div>
      <div className="flex items-center gap-3 pt-3.5 mt-4 border-t border-[#DAD5CA]">
        <Button type="submit" disabled={submitting || filled === 0} className="btn-industrial h-[38px]" style={{ width: 150 }} data-testid="save-template-btn">
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEdit ? 'Save changes' : 'Save template'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="h-[38px]">Cancel</Button>
        <span className="ml-auto text-[#8F8A7F]" style={{ fontSize: 12 }}>{filled === 0 ? 'Add at least one step' : `${fmtDur(total)}${hasUntimed({ steps: steps.map(s => ({ estimated_minutes: s.estimated_minutes === '' ? null : 1 })) }) ? '+' : ''} total`}</span>
      </div>
    </form>
  );
};

// ── Page ─────────────────────────────────────────────────────────────
export const TaskLibraryPage = () => {
  const { confirm, dialogProps } = useConfirm();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [sort, setSort] = useState('name');
  const [drawer, setDrawer] = useState(null);
  const [form, setForm] = useState(null); // {existing} | {}

  useEffect(() => { load(); }, []);
  const load = async (openId) => {
    try { const r = await taskLibraryApi.list(); setTemplates(r.data || []); if (openId) { const t = (r.data || []).find(x => x.id === openId); if (t) setDrawer(t); } }
    catch { toast.error('Failed to load task templates'); } finally { setLoading(false); }
  };

  const categories = useMemo(() => [...new Set(templates.map(t => normalizeCat(t.category)))], [templates]);
  const catCount = (c) => templates.filter(t => normalizeCat(t.category) === c).length;
  const filtered = useMemo(() => {
    let list = templates.filter(t => {
      const q = search.toLowerCase();
      const matchesSearch = !q || t.name?.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q) || stepsOf(t).some(s => (s.description || '').toLowerCase().includes(q) || (s.notes || '').toLowerCase().includes(q));
      const matchesCat = catFilter === 'all' || normalizeCat(t.category) === catFilter;
      return matchesSearch && matchesCat;
    });
    list.sort((a, b) => sort === 'duration' ? totalMins(b) - totalMins(a) : (a.name || '').localeCompare(b.name || ''));
    return list;
  }, [templates, search, catFilter, sort]);

  const grandSteps = templates.reduce((a, t) => a + stepsOf(t).length, 0);
  const grandMins = templates.reduce((a, t) => a + totalMins(t), 0);
  const hasFilters = search || catFilter !== 'all';

  const handleDelete = (t) => confirm({
    title: 'Delete this task template?', description: `"${t.name}" will be permanently removed.`,
    footnote: 'Work orders already built from it keep their steps and are unaffected.', testId: 'confirm-delete-task-template',
    onConfirm: async () => { try { await taskLibraryApi.delete(t.id); toast.success('Template deleted'); setDrawer(null); load(); } catch (e) { toast.error('Failed to delete'); throw e; } },
  });
  const handleDuplicate = async (t) => {
    try {
      const steps = stepsOf(t).map(s => ({ description: s.description, estimated_minutes: s.estimated_minutes ?? null, notes: s.notes || null }));
      const r = await taskLibraryApi.create({ name: `${t.name} (copy)`, description: t.description || '', category: t.category || 'General', steps });
      toast.success('Duplicated'); setDrawer(null); load(r.data?.id);
    } catch { toast.error('Failed to duplicate'); }
  };
  const addToWorkOrder = (t) => { setDrawer(null); navigate(`/work-orders?new=true&template=${t.id}`); };

  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="task-library-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Task Library</h1>
          <p className="text-muted-foreground">Standard job procedures your workshop applies to work orders</p>
        </div>
        <Button onClick={() => setForm({})} className="btn-industrial" data-testid="create-template-btn"><Plus className="w-5 h-5 mr-2" />New template</Button>
      </div>

      <div className="bg-white overflow-hidden" style={{ border: '1px solid #DAD5CA', borderRadius: 12, boxShadow: '0 1px 2px rgba(26,26,26,0.05), 0 8px 20px -14px rgba(26,26,26,0.16)' }}>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3" style={{ padding: '13px 14px', borderBottom: '1px solid #E6E2D9' }}>
          <div className="relative w-full lg:max-w-[400px] lg:min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8F8A7F]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Template name or step text" data-testid="template-search" className="pl-10 pr-14 h-[42px] border-[1.5px] border-[#1A1A1A] rounded-[9px]" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[#8F8A7F]" style={{ fontSize: 10.5 }}>{filtered.length}/{templates.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Sort</span>
            <FilterPills value={sort} onChange={setSort} testId="task-sort" options={[{ id: 'name', label: 'Name', testId: 'sort-name' }, { id: 'duration', label: 'Duration', testId: 'sort-duration' }]} />
          </div>
        </div>
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2" style={{ padding: '11px 14px', borderBottom: '1px solid #E6E2D9' }}>
            <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Category</span>
            <FilterPills value={catFilter} onChange={setCatFilter} testId="category-filter" options={[{ id: 'all', label: 'All', count: templates.length, testId: 'cat-all' }, ...categories.map(c => ({ id: c, label: c, count: catCount(c), testId: `cat-${c}` }))]} />
            {hasFilters && <button onClick={() => { setSearch(''); setCatFilter('all'); }} className="text-[#8F8A7F] hover:text-[#1A1A1A] flex items-center gap-1" style={{ fontSize: 12 }} data-testid="clear-filters-btn"><X className="w-3.5 h-3.5" />Clear filters</button>}
          </div>
        )}

        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Wrench className="w-12 h-12 mx-auto text-[#C9C3B6] mb-3" />
              <h3 className="font-bold text-[#1A1A1A]" style={{ fontSize: 16 }}>{hasFilters ? 'No templates found' : 'No templates yet'}</h3>
              <p className="text-[#8F8A7F] mt-1" style={{ fontSize: 12.5 }}>{hasFilters ? 'No task templates match the current filters.' : 'Capture a standard job procedure so your workshop applies it the same way every time.'}</p>
              {hasFilters ? <Button variant="outline" className="mt-4" onClick={() => { setSearch(''); setCatFilter('all'); }}>Clear filters</Button>
                : <Button className="btn-industrial mt-4" onClick={() => setForm({})}><Plus className="w-4 h-4 mr-2" />New template</Button>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div style={{ minWidth: 1240 }}>
                <div className="grid items-center bg-[#F6F4EF] uppercase tracking-[0.1em] text-[#6B6660]" style={{ gridTemplateColumns: 'minmax(220px,1.4fr) 138px minmax(180px,1.2fr) 70px 96px 120px 118px 84px', height: 32, padding: '0 14px', fontSize: 10, borderBottom: '1px solid #DAD5CA' }}>
                  <span>Template</span><span>Category</span><span>Description</span><span>Steps</span><span>Est. time</span><span>Used</span><span>Created by</span><span />
                </div>
                {filtered.map(t => (
                  <div key={t.id} onClick={() => setDrawer(t)} data-testid={`template-row-${t.id}`} className="grid items-center cursor-pointer transition-colors hover:bg-[#FFFCEF]" style={{ gridTemplateColumns: 'minmax(220px,1.4fr) 138px minmax(180px,1.2fr) 70px 96px 120px 118px 84px', padding: '9px 14px', borderBottom: '1px solid #F0EDE6' }}>
                    <div className="flex items-center gap-2.5 min-w-0 pr-2">
                      <span className="flex items-center justify-center shrink-0" style={{ width: 30, height: 30, background: '#F6F4EF', border: '1px solid #E6E2D9', borderRadius: 8 }}><Wrench className="w-4 h-4" style={{ color: '#6B6660' }} /></span>
                      <span className="font-semibold text-[#1A1A1A] truncate" style={{ fontSize: 13 }}>{t.name}</span>
                    </div>
                    <div><CategoryPill category={t.category} /></div>
                    <span className="truncate pr-2" style={{ fontSize: 12.5, color: t.description ? '#6B6660' : '#B5AFA3' }}>{t.description || 'No description'}</span>
                    <span className="font-mono" style={{ fontSize: 12.5, color: '#3D3A35' }}>{stepsOf(t).length}</span>
                    <span className="font-mono font-semibold" style={{ fontSize: 12.5, color: '#1A1A1A' }}>{fmtDur(totalMins(t))}{hasUntimed(t) ? '+' : ''}</span>
                    <div className="min-w-0 pr-2" data-testid={`template-used-${t.id}`}>
                      <div className="font-mono font-semibold" style={{ fontSize: 12.5, color: t.times_used ? '#1A1A1A' : '#B5AFA3' }}>{usedLabel(t)}</div>
                      <div className="truncate" style={{ fontSize: 10.5, color: '#A39D91' }}>{t.last_used_at ? formatDate(t.last_used_at) : 'Never used'}</div>
                    </div>
                    <span className="truncate" style={{ fontSize: 12, color: '#6B6660' }}>{t.created_by_name || '—'}</span>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); addToWorkOrder(t); }}
                        title="Add to work order"
                        data-testid={`row-add-to-wo-${t.id}`}
                        className="w-8 h-8 rounded-md flex items-center justify-center text-[#6B6660] hover:text-[#1A1A1A] hover:bg-[#F1EFEA] transition-colors"
                      >
                        <Wrench className="w-[15px] h-[15px]" />
                      </button>
                      <ChevronRight className="w-[15px] h-[15px]" style={{ color: '#B5AFA3' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        <div className="flex items-center justify-between" style={{ padding: '11px 14px', borderTop: '1px solid #E6E2D9', background: '#FBFAF7' }}>
          <span className="text-[#6B6660]" style={{ fontSize: 12 }}>Showing {filtered.length} of {templates.length} templates</span>
          <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>{grandSteps} steps · {fmtDur(grandMins)} of standard work captured</span>
        </div>
      </div>

      {/* Detail sheet */}
      <SlideDrawer open={!!drawer} onOpenChange={(o) => { if (!o) setDrawer(null); }} title="Task template" dismissable
        titleExtra={drawer && <span className="font-mono text-[#8F8A7F]" style={{ fontSize: 11 }}>{fmtDur(totalMins(drawer))}{hasUntimed(drawer) ? '+' : ''}</span>} size="lg">
        {drawer && <DetailBody t={drawer} formatDate={formatDate} onEdit={() => { const t = drawer; setDrawer(null); setForm({ existing: t }); }} onDuplicate={() => handleDuplicate(drawer)} onDelete={() => handleDelete(drawer)} onAddToWorkOrder={() => addToWorkOrder(drawer)} />}
      </SlideDrawer>

      {/* Form sheet */}
      <SlideDrawer open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }} title={form?.existing ? 'Edit task template' : 'New task template'} dismissable size="lg">
        {form && <FormBody existing={form.existing} onCancel={() => setForm(null)} onSuccess={() => { setForm(null); load(); }} />}
      </SlideDrawer>

      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

export default TaskLibraryPage;
